// src/components/switchCalendar/SwitchCalendarView.jsx

import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Chip,
  Paper,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  useTheme,
  Tooltip,
  Button,
  Badge,
  Checkbox,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Today,
  NotificationsActive,
  CheckCircle,
  RadioButtonUnchecked,
  Refresh,
} from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { createSwitchCalendarEvent } from "../../utils/models/switchCalendarModel";
import LoadingSpinner from "../ui/LoadingSpinner";

const URGENCY_COLORS = {
  overdue: "#F44336",
  urgent: "#FF9800",
  warning: "#2196F3",
  pending: "#9E9E9E",
  completed: "#4CAF50",
};

const SwitchCalendarView = () => {
  const theme = useTheme();
  const { data: switches } = useCollection("switches");
  const { data: calendarEvents, loading } = useCollection("switchCalendar");
  const { data: suppliers } = useCollection("switchSuppliers");
  const { add, update } = useAuditFirestore("switchCalendar");
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSupplier, setFilterSupplier] = useState("all");
  const [showCompleted, setShowCompleted] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [localEvents, setLocalEvents] = useState(null);

  useEffect(() => {
    if (calendarEvents) setLocalEvents(calendarEvents);
  }, [calendarEvents]);

  // src/components/switchCalendar/SwitchCalendarView.jsx
// Actualiza la función syncCalendar:

const syncCalendar = async () => {
    if (!switches || !calendarEvents) return;
    
    setSyncing(true);
    try {
      const activeSwitches = switches.filter(
        (s) => s.isActive !== false && !s.fechaSalida && s.fechaEntrada
      );

      for (const switchItem of activeSwitches) {
        const fechaEntrada = new Date(switchItem.fechaEntrada + "T00:00:00");
        const fechaAniversario = new Date(fechaEntrada);
        fechaAniversario.setFullYear(fechaAniversario.getFullYear() + 1);
        
        const fechaAlerta = new Date(fechaAniversario);
        fechaAlerta.setMonth(fechaAlerta.getMonth() - 1);

        // Verificar si ya existe un evento no completado para este switch
        const existingEvent = calendarEvents.find(
          (e) => e.switchId === switchItem.id && !e.completado && e.isActive !== false
        );

        if (!existingEvent) {
          console.log("Creando evento para:", switchItem.switchClientName, "Aniversario:", fechaAniversario);
          
          const newEvent = createSwitchCalendarEvent({
            switchId: switchItem.id,
            switchClientId: switchItem.switchClientId,
            switchClientName: switchItem.switchClientName,
            supplierId: switchItem.supplierId,
            supplierName: switchItem.supplierName,
            fechaEntrada: switchItem.fechaEntrada,
            fechaAlerta: fechaAlerta.toISOString().split('T')[0],
            fechaAniversario: fechaAniversario.toISOString().split('T')[0],
          });

          await add(newEvent);
        }
      }

      // Marcar completados los eventos cuyos switches ya no están activos
      const activeSwitchIds = activeSwitches.map((s) => s.id);
      for (const event of calendarEvents) {
        if (!event.completado && !activeSwitchIds.includes(event.switchId)) {
          await update(event.id, {
            completado: true,
            fechaCompletado: new Date().toISOString().split('T')[0],
            updatedAt: new Date().toISOString(),
          });
        }
      }

      // Refrescar eventos
      setLocalEvents([...calendarEvents]);
    } catch (error) {
      console.error("Error syncing calendar:", error);
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleComplete = async (event) => {
    const updatedEvent = {
      ...event,
      completado: !event.completado,
      fechaCompletado: !event.completado ? new Date().toISOString().split('T')[0] : '',
      updatedAt: new Date().toISOString(),
    };

    setLocalEvents((prev) => prev.map((e) => (e.id === event.id ? updatedEvent : e)));

    try {
      await update(event.id, {
        completado: updatedEvent.completado,
        fechaCompletado: updatedEvent.fechaCompletado,
        updatedAt: updatedEvent.updatedAt,
      });
    } catch (error) {
      console.error("Error updating event:", error);
      setLocalEvents((prev) => prev.map((e) => (e.id === event.id ? event : e)));
    }
  };

  const getUrgencyLevel = (event) => {
  if (event.completado) return "completed"; // Verde
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const aniversario = new Date(event.fechaAniversario + "T00:00:00");
  const diffTime = aniversario - today;
  const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (daysUntil <= 0) return "overdue";    // Rojo - Ya pasó
  if (daysUntil <= 30) return "urgent";    // Naranja - 30 días o menos
  if (daysUntil <= 60) return "warning";   // Azul - 31-60 días
  return "pending";                        // Gris - Más de 60 días
};

// En SwitchCalendarView.jsx, busca donde están las otras funciones y añade:

const getDaysUntil = (dateString) => {
  if (!dateString) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(dateString + "T00:00:00");
  const diffTime = targetDate - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString + "T00:00:00").toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

  const uniqueSuppliers = useMemo(() => {
    if (!suppliers) return [];
    return suppliers.filter((s) => s.isActive !== false);
  }, [suppliers]);

  const filteredEvents = useMemo(() => {
    if (!localEvents) return [];
    return localEvents.filter((event) => {
      if (event.isActive === false) return false;
      
      const urgency = getUrgencyLevel(event);
      
      if (!showCompleted && event.completado) return false;
      
      const matchesStatus = filterStatus === "all" ||
        (filterStatus === "overdue" && urgency === "overdue") ||
        (filterStatus === "urgent" && urgency === "urgent") ||
        (filterStatus === "warning" && urgency === "warning") ||
        (filterStatus === "pending" && urgency === "pending") ||
        (filterStatus === "completed" && event.completado);
      
      if (!matchesStatus) return false;
      
      const matchesSupplier = filterSupplier === "all" || event.supplierId === filterSupplier;
      
      return matchesSupplier;
    });
  }, [localEvents, filterStatus, filterSupplier, showCompleted]);

  const daysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

  const getMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const days = daysInMonth(currentDate);
    const firstDay = new Date(year, month, 1).getDay();
    const firstDayOfWeek = firstDay === 0 ? 6 : firstDay - 1;
    const calendarDays = [];
    const prevMonthDays = daysInMonth(new Date(year, month, 0));
    
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      calendarDays.push({ date: new Date(year, month - 1, prevMonthDays - i), isCurrentMonth: false });
    }
    for (let i = 1; i <= days; i++) {
      calendarDays.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    const remainingDays = 42 - calendarDays.length;
    for (let i = 1; i <= remainingDays; i++) {
      calendarDays.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    return calendarDays;
  };

  const getEventsForDate = (date) => {
    return filteredEvents.filter((event) => {
      const eventDate = new Date(event.fechaAniversario);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const getAlertEventsForDate = (date) => {
    return filteredEvents.filter((event) => {
      const alertDate = new Date(event.fechaAlerta);
      return alertDate.toDateString() === date.toDateString() && !event.completado;
    });
  };

  const changeMonth = (increment) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + increment);
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  const dayNames = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  const isCurrentMonthAndYear = () => {
    const now = new Date();
    return currentDate.getMonth() === now.getMonth() && currentDate.getFullYear() === now.getFullYear();
  };

  if (loading) return <LoadingSpinner />;
  const calendarDays = getMonthDays();

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      {/* Header con navegación y filtros */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2, md: 2.5 },
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: "16px", sm: "24px" },
          border: "1px solid",
          borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(226,232,240,0.8)",
          background: theme.palette.mode === "dark"
            ? "linear-gradient(145deg, rgba(20,20,20,0.95), rgba(30,30,30,0.9))"
            : "linear-gradient(145deg, #ffffff, #f8fafc)",
          overflow: "hidden",
        }}
      >
        <Stack spacing={1.5}>
          {/* Navegación */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={{ xs: 0.5, sm: 1 }}
              sx={{
                px: { xs: 0.5, sm: 1 },
                py: 0.5,
                borderRadius: "18px",
                background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)",
                border: "1px solid",
                borderColor: theme.palette.divider,
              }}
            >
              <IconButton onClick={() => changeMonth(-1)} size="small" sx={{ borderRadius: "12px" }}>
                <ChevronLeft fontSize="small" />
              </IconButton>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  minWidth: { xs: 140, sm: 180, md: 220 },
                  textAlign: "center",
                  fontSize: { xs: "0.85rem", sm: "1rem", md: "1.25rem" },
                  px: { xs: 0.5, sm: 1 },
                }}
              >
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </Typography>
              <IconButton onClick={() => changeMonth(1)} size="small" sx={{ borderRadius: "12px" }}>
                <ChevronRight fontSize="small" />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<Refresh />}
                onClick={syncCalendar}
                disabled={syncing}
                sx={{
                  height: { xs: 36, sm: 42 },
                  px: { xs: 1.5, sm: 2 },
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  boxShadow: "none",
                  background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                  "&:hover": { boxShadow: `0 4px 12px ${theme.palette.info.main}40` },
                }}
              >
                {syncing ? "Sincronizando..." : "Sincronizar"}
              </Button>

              <Button
                variant={isCurrentMonthAndYear() ? "outlined" : "contained"}
                size="small"
                startIcon={<Today />}
                onClick={goToToday}
                sx={{
                  height: { xs: 36, sm: 42 },
                  px: { xs: 1.5, sm: 2 },
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  boxShadow: "none",
                }}
              >
                Hoy
              </Button>
            </Stack>
          </Stack>

          {/* Filtros */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr auto auto" },
              gap: 1,
              alignItems: "center",
            }}
          >
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>Estado</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Estado"
                sx={{ borderRadius: "12px", height: 44, fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="overdue">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: URGENCY_COLORS.overdue }} />
                    Vencidos
                  </Box>
                </MenuItem>
                <MenuItem value="urgent">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: URGENCY_COLORS.urgent }} />
                    Urgentes
                  </Box>
                </MenuItem>
                <MenuItem value="warning">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: URGENCY_COLORS.warning }} />
                    Próximos
                  </Box>
                </MenuItem>
                <MenuItem value="pending">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: URGENCY_COLORS.pending }} />
                    Pendientes
                  </Box>
                </MenuItem>
                <MenuItem value="completed">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: URGENCY_COLORS.completed }} />
                    Completados
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>Comercializadora</InputLabel>
              <Select
                value={filterSupplier}
                onChange={(e) => setFilterSupplier(e.target.value)}
                label="Comercializadora"
                sx={{ borderRadius: "12px", height: 44, fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
              >
                <MenuItem value="all">Todas</MenuItem>
                {uniqueSuppliers.map((supplier) => (
                  <MenuItem key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Paper
              elevation={0}
              sx={{
                px: { xs: 1, sm: 1.5 },
                py: 0.5,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                height: 44,
                background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)",
                border: "1px solid",
                borderColor: theme.palette.divider,
              }}
            >
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    checked={showCompleted}
                    onChange={(e) => setShowCompleted(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>
                    {showCompleted ? "Completados" : "Ocultar compl."}
                  </Typography>
                }
              />
            </Paper>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: 44 }}>
              <Chip
                label={`${filteredEvents.length} eventos`}
                size="small"
                sx={{
                  height: 32,
                  borderRadius: "10px",
                  fontWeight: 700,
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                  background: theme.palette.background.paper,
                  border: "1px solid",
                  borderColor: theme.palette.info.main,
                  color: theme.palette.info.main,
                }}
              />
            </Box>
          </Box>
        </Stack>
      </Paper>

      {/* Calendario */}
      <Card
        elevation={2}
        sx={{
          borderRadius: { xs: 1, sm: 2 },
          overflow: "hidden",
          border: "1px solid",
          borderColor: theme.palette.divider,
          background: theme.palette.background.paper,
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {/* Días de la semana */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", width: "100%" }}>
          {dayNames.map((day, index) => {
            const today = new Date();
            const isTodayColumn = index === (today.getDay() === 0 ? 6 : today.getDay() - 1);
            return (
              <Box
                key={day}
                sx={{
                  p: { xs: 1, sm: 1.5 },
                  textAlign: "center",
                  backgroundColor: isTodayColumn
                    ? theme.palette.mode === "dark"
                      ? "rgba(0, 188, 212, 0.2)"
                      : "rgba(0, 188, 212, 0.1)"
                    : theme.palette.mode === "dark"
                    ? "rgba(0, 188, 212, 0.1)"
                    : "rgba(0, 188, 212, 0.05)",
                  borderBottom: "2px solid",
                  borderColor: isTodayColumn ? theme.palette.info.main : theme.palette.divider,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={isTodayColumn ? 700 : 600}
                  fontSize={{ xs: "0.6rem", sm: "0.75rem" }}
                  color={theme.palette.info.main}
                  sx={{ display: { xs: "none", sm: "block" } }}
                >
                  {day}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={isTodayColumn ? 700 : 600}
                  fontSize="0.65rem"
                  color={theme.palette.info.main}
                  sx={{ display: { xs: "block", sm: "none" } }}
                >
                  {day.charAt(0)}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Días del mes */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", width: "100%" }}>
          {calendarDays.map((day, index) => {
            const dayEvents = getEventsForDate(day.date);
            const alertEvents = getAlertEventsForDate(day.date);
            const isToday = day.date.toDateString() === new Date().toDateString();
            const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;

            return (
              <Box
                key={index}
                sx={{
                  minHeight: { xs: 70, sm: 90, md: 110 },
                  p: { xs: 0.5, sm: 1 },
                  borderBottom: "1px solid",
                  borderRight: "1px solid",
                  borderColor: theme.palette.divider,
                  position: "relative",
                  backgroundColor: !day.isCurrentMonth
                    ? theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.02)"
                      : "rgba(0,0,0,0.02)"
                    : isToday
                    ? theme.palette.mode === "dark"
                      ? "rgba(0, 188, 212, 0.15)"
                      : "rgba(0, 188, 212, 0.08)"
                    : isWeekend
                    ? theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(0,0,0,0.01)"
                    : "transparent",
                  "&:nth-of-type(7n)": { borderRight: "none" },
                  overflow: "hidden",
                  ...(isToday && {
                    boxShadow: `inset 0 0 0 2px ${theme.palette.info.main}`,
                    borderRadius: "4px",
                  }),
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {isToday ? (
                      <Badge
                        overlap="circular"
                        variant="dot"
                        color="info"
                        sx={{ "& .MuiBadge-dot": { width: { xs: 6, sm: 10 }, height: { xs: 6, sm: 10 } } }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            backgroundColor: theme.palette.info.main,
                            color: "white",
                            width: { xs: 18, sm: 24 },
                            height: { xs: 18, sm: 24 },
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: { xs: "0.6rem", sm: "0.75rem" },
                          }}
                        >
                          {day.date.getDate()}
                        </Typography>
                      </Badge>
                    ) : (
                      <Typography
                        variant="caption"
                        fontWeight={400}
                        color={!day.isCurrentMonth ? "text.disabled" : "text.primary"}
                        sx={{
                          width: { xs: 18, sm: 24 },
                          height: { xs: 18, sm: 24 },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: { xs: "0.6rem", sm: "0.75rem" },
                        }}
                      >
                        {day.date.getDate()}
                      </Typography>
                    )}
                  </Box>
                  {dayEvents.length > 0 && (
                    <Chip
                      label={dayEvents.length}
                      size="small"
                      sx={{
                        height: { xs: 14, sm: 16 },
                        minWidth: { xs: 14, sm: 16 },
                        fontSize: { xs: "0.5rem", sm: "0.55rem" },
                        fontWeight: 600,
                        bgcolor: theme.palette.info.main,
                        color: "white",
                      }}
                    />
                  )}
                </Box>

                <Stack spacing={0.3} sx={{ overflow: "hidden" }}>
  {/* Alertas (1 mes antes) */}
  {alertEvents.map((event) => {
    const urgency = getUrgencyLevel(event);
    const daysUntil = getDaysUntil(event.fechaAniversario);
    return (
      <Tooltip key={`alert-${event.id}`} title={`🔔 Alerta: ${event.switchClientName} - ${event.supplierName} (Aniversario: ${formatDate(event.fechaAniversario)})`} arrow placement="top">
        <Box
          onClick={() => handleToggleComplete(event)}
          sx={{
            p: { xs: 0.3, sm: 0.5 },
            borderRadius: "4px",
            backgroundColor: event.completado
              ? URGENCY_COLORS.completed
              : URGENCY_COLORS[urgency],
            color: "white",
            cursor: "pointer",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            maxWidth: "100%",
            display: "flex",
            alignItems: "center",
            gap: 0.3,
            opacity: event.completado ? 0.7 : 1,
            textDecoration: event.completado ? "line-through" : "none",
          }}
        >
          <NotificationsActive sx={{ fontSize: { xs: 8, sm: 10 } }} />
          <Typography
            variant="caption"
            sx={{
              color: "white",
              fontSize: { xs: "0.5rem", sm: "0.6rem", md: "0.65rem" },
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
          >
            {event.switchClientName}
          </Typography>
          <Chip
            label="Falta 1 mes"
            size="small"
            sx={{
              height: { xs: 12, sm: 14 },
              fontSize: { xs: "0.45rem", sm: "0.5rem" },
              backgroundColor: "rgba(255,255,255,0.3)",
              color: "white",
              fontWeight: 600,
              ml: "auto",
              "& .MuiChip-label": { px: 0.5 },
            }}
          />
        </Box>
      </Tooltip>
    );
  })}

  {/* Aniversarios */}
  {dayEvents.map((event) => {
    const urgency = getUrgencyLevel(event);
    const daysUntil = getDaysUntil(event.fechaAniversario);
    return (
      <Tooltip
        key={event.id}
        title={`${event.completado ? "✅" : "📅"} ${event.switchClientName} - ${event.supplierName}${event.completado ? " (Completado)" : ` (${daysUntil !== null ? `${daysUntil} días` : ""})`}`}
        arrow
        placement="top"
      >
        <Box
          onClick={() => handleToggleComplete(event)}
          sx={{
            p: { xs: 0.3, sm: 0.5 },
            borderRadius: "4px",
            backgroundColor: event.completado
              ? URGENCY_COLORS.completed
              : URGENCY_COLORS[urgency],
            color: "white",
            cursor: "pointer",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            maxWidth: "100%",
            display: "flex",
            alignItems: "center",
            gap: 0.3,
            opacity: event.completado ? 0.7 : 1,
            textDecoration: event.completado ? "line-through" : "none",
          }}
        >
          <Checkbox
            checked={event.completado}
            size="small"
            icon={<RadioButtonUnchecked sx={{ fontSize: { xs: 8, sm: 10 }, color: "white" }} />}
            checkedIcon={<CheckCircle sx={{ fontSize: { xs: 8, sm: 10 }, color: "white" }} />}
            sx={{ p: 0, m: 0 }}
          />
          <Typography
            variant="caption"
            sx={{
              color: "white",
              fontSize: { xs: "0.5rem", sm: "0.6rem", md: "0.65rem" },
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
          >
            {event.switchClientName}
          </Typography>
          <Chip
            label="Aniversario"
            size="small"
            sx={{
              height: { xs: 12, sm: 14 },
              fontSize: { xs: "0.45rem", sm: "0.5rem" },
              backgroundColor: "rgba(255,255,255,0.3)",
              color: "white",
              fontWeight: 600,
              ml: "auto",
              "& .MuiChip-label": { px: 0.5 },
            }}
          />
        </Box>
      </Tooltip>
    );
  })}
</Stack>
              </Box>
            );
          })}
        </Box>
      </Card>
    </Box>
  );
};

export default SwitchCalendarView;